/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  FilePenLine,
  FileText,
  Image,
  Play,
  Plus,
  Ruler,
  ScrollText,
  Search,
  Table2,
  TriangleAlert,
} from 'lucide-react';

import { CardListSkeleton, Text } from '@/shared/ui';

import { useIsMobile } from '@/shared/hooks';

import { LaunchesService } from '../../services/launchesApi';
import { useLaunchMaterials } from '../../hooks/useLaunchMaterials';
import { useLaunchPriceTables } from '../../hooks/useLaunchPriceTables';
import type { LaunchMaterial, MaterialCategory } from '../../types';
import { AddMaterialSheet } from './AddMaterialSheet';
import { MaterialCard } from './MaterialCard';
import { MaterialViewer } from './MaterialViewer';
import { PriceTablePanel, StalePriceBanner } from './PriceTablePanel';

/*
 * Geometry and type are authored inline, as the prototype authors them. A
 * freshly written arbitrary Tailwind class is not reliably generated in
 * this project, so a `py-[var(--space-1-5)]` can quietly produce no rule at
 * all; inline style needs nothing generated.
 */

/** Category icons, matching the prototype's choices. */
const CATEGORY_ICON: Record<string, typeof BookOpen> = {
  price_table: Table2,
  sales_book: BookOpen,
  floor_plans: Ruler,
  specs: ScrollText,
  contract_template: FilePenLine,
  media: Image,
};

interface LaunchMaterialsTabProps {
  agencyId: string;
  launchId: string;
  canManage: boolean;
}

/**
 * Category name.
 */
function categoryName(
  category: MaterialCategory,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  return category.fixed ? t(`materials.categories.${category.category_key}`) : (category.label ?? '');
}

/**
 * One category in the rail: what it is, and what is inside it.
 *
 * A row rather than an expanded section because the library is navigated,
 * not scrolled: a launch with six categories and forty files would be an
 * unreadable wall if every section were open at once. The price table says
 * which version is in force rather than how many files it has — a count of
 * one was never the thing a broker needed to know before negotiating.
 */
function CategoryRow({ category, count, brokenCount, active, compact, priceLine, onOpen, t }: {
  category: MaterialCategory;
  count: number;
  brokenCount: number;
  active: boolean;
  compact: boolean;
  priceLine?: string | null;
  onOpen: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const Icon = CATEGORY_ICON[category.category_key] ?? BookOpen;

  const subtitle = priceLine ?? (count === 0
    ? t('materials.emptyShort')
    : [
        t('materials.itemCount', { count }),
        brokenCount ? t('materials.brokenCount', { count: brokenCount }) : '',
      ].filter(Boolean).join(' · '));

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="launch-material-category"
      style={{
        minHeight: compact ? 'var(--space-12)' : 'var(--space-16)',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: compact ? 'var(--space-1-5) var(--space-3)' : 'var(--space-2) var(--space-1)',
        border: 'none',
        borderRadius: compact ? 'var(--radius-md)' : 0,
        borderBottom: compact ? undefined : '1px solid var(--border-ultra-subtle)',
        background: active ? 'var(--selection-selected)' : 'transparent',
        color: active ? 'var(--seed-base)' : 'var(--text-primary)',
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <Icon size={18} color={active ? 'var(--seed-base)' : 'var(--text-secondary)'} />

      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Text
          as="span"
          style={{
            fontSize: 'var(--text-body-sm)',
            fontWeight: 'var(--font-semibold)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {categoryName(category, t)}
        </Text>
        <Text as="span" style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)' }}>
          {subtitle}
        </Text>
      </span>

      {brokenCount > 0 && (
        <span style={{ display: 'flex', color: 'var(--launch-material-broken-fg)' }}>
          <TriangleAlert size={15} />
        </span>
      )}

      {!compact && <ChevronRight size={16} color="var(--text-tertiary)" />}
    </button>
  );
}

/**
 * What an empty category looks like.
 *
 * A sentence alone in a white panel reads as a page that failed to load.
 * The dashed outline says the space is meant to hold something, and the
 * manager gets the action right where the absence is.
 */
function EmptyCategory({ category, canManage, onAdd, t }: {
  category: MaterialCategory;
  canManage: boolean;
  onAdd: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const Icon = CATEGORY_ICON[category.category_key] ?? BookOpen;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-12) var(--space-4)',
        borderRadius: 'var(--radius-lg)',
        border: '1px dashed var(--border-medium)',
        textAlign: 'center',
      }}
    >
      <span style={{ color: 'var(--text-tertiary)', display: 'flex' }}>
        <Icon size={28} strokeWidth={1.5} />
      </span>

      <Text
        as="span"
        style={{
          fontSize: 'var(--text-body)',
          fontWeight: 'var(--font-semibold)',
          color: 'var(--text-primary)',
        }}
      >
        {t('materials.emptyCategoryTitle')}
      </Text>

      <Text
        as="span"
        style={{
          fontSize: 'var(--text-small)',
          color: 'var(--text-secondary)',
          maxWidth: '46ch',
        }}
      >
        {canManage ? t('materials.emptyBodyManager') : t('materials.emptyBodyBroker')}
      </Text>

      {canManage && (
        <button
          type="button"
          onClick={onAdd}
          style={{
            marginTop: 'var(--space-2)',
            height: 'var(--space-10)',
            padding: '0 var(--space-5)',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: 'var(--button-primary-bg)',
            color: 'var(--button-primary-text)',
            fontSize: 'var(--text-body-sm)',
            fontWeight: 'var(--font-semibold)',
            cursor: 'pointer',
          }}
        >
          {t('materials.add')}
        </button>
      )}
    </div>
  );
}

/**
 * Search results, as one list rather than a grid of cards.
 *
 * A search crosses categories, so the useful column is where each hit
 * lives — not a preview plate repeated eight times down the page.
 */
function SearchResults({ query, results, labelFor, onOpen, t }: {
  query: string;
  results: LaunchMaterial[];
  /** Where the hit lives, already translated. */
  labelFor: (categoryKey: string) => string;
  onOpen: (material: LaunchMaterial) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <Text
        as="span"
        style={{
          fontSize: 'var(--text-app-section)',
          fontWeight: 'var(--font-semibold)',
          color: 'var(--text-primary)',
        }}
      >
        {t('materials.searchResultsFor', { query })}
      </Text>

      <div
        style={{
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-subtle)',
          background: 'var(--surface-card)',
          overflow: 'hidden',
        }}
      >
        {results.length === 0 ? (
          <div
            style={{
              padding: 'var(--space-6)',
              textAlign: 'center',
              fontSize: 'var(--text-body-sm)',
              color: 'var(--text-secondary)',
            }}
          >
            {t('materials.noResults')}
          </div>
        ) : (
          results.map((material) => (
            <button
              key={material.material_id}
              type="button"
              onClick={() => onOpen(material)}
              style={{
                width: '100%',
                minHeight: 'var(--space-14)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-2) var(--space-4)',
                border: 'none',
                borderBottom: '1px solid var(--border-subtle)',
                background: 'transparent',
                textAlign: 'left',
                color: 'var(--text-primary)',
                cursor: 'pointer',
              }}
            >
              <span style={{ color: 'var(--text-secondary)', display: 'flex' }}>
                {material.source === 'embed' ? <Play size={18} /> : <FileText size={18} />}
              </span>
              <Text
                as="span"
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 'var(--text-body-sm)',
                  fontWeight: 'var(--font-semibold)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {material.title}
              </Text>
              <Text
                as="span"
                style={{
                  fontSize: 'var(--text-small)',
                  color: 'var(--text-tertiary)',
                  flexShrink: 0,
                }}
              >
                {labelFor(material.category)}
              </Text>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

/**
 * The launch's sales library.
 *
 * Two panels on the desktop: a rail that holds the search, the categories
 * and the publish action, and a panel that shows one category at a time.
 * The rail is navigation, not a step, so it stays on screen and sticks as
 * the panel scrolls. On the phone the list gives way to the category,
 * because 280px of rail would leave nothing for the items.
 */
export function LaunchMaterialsTab({
  agencyId,
  launchId,
  canManage,
}: LaunchMaterialsTabProps) {
  const { t } = useTranslation('features/launches');
  const { materials, categories, isLoading, isError, retry } = useLaunchMaterials(agencyId, launchId);
  const [query, setQuery] = useState('');
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const isMobile = useIsMobile();
  const [viewing, setViewing] = useState<LaunchMaterial | null>(null);
  const priceTables = useLaunchPriceTables(agencyId, launchId);

  const countsByCategory = useMemo(() => {
    const acc: Record<string, { total: number; broken: number }> = {};
    materials.forEach((m) => {
      const entry = acc[m.category] ?? { total: 0, broken: 0 };
      entry.total += 1;
      if (m.source === 'embed' && (m.embed_status ?? 'ok') !== 'ok') entry.broken += 1;
      acc[m.category] = entry;
    });
    return acc;
  }, [materials]);

  const searchResults = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    return materials.filter((m) => {
      const category = categories.find((c) => c.category_key === m.category);
      const label = category ? categoryName(category, t).toLowerCase() : '';
      return m.title.toLowerCase().includes(needle) || label.includes(needle);
    });
  }, [materials, categories, query, t]);

  /**
   * Renames a material, then reloads the library so every surface showing
   * that name — the card, the price-table headline, the search hit — agrees
   * on it. Rejects on failure so the field stays open with the typed value.
   */
  const renameMaterial = useCallback(
    async (material: LaunchMaterial, title: string) => {
      await LaunchesService.updateMaterial(agencyId, launchId, material.material_id, { title });
      retry();
      priceTables.retry();
    },
    [agencyId, launchId, retry, priceTables],
  );

  /** Opening the current table is what clears the stale warning. */
  const openPriceVersion = (version: number) => {
    const row = version === priceTables.current?.version
      ? priceTables.current
      : priceTables.history.find((h) => h.version === version);
    const material = materials.find((m) => m.material_id === row?.material_id);
    if (material) setViewing(material);
    if (version === priceTables.current?.version) priceTables.markSeen(version);
  };

  // The app loads lists with CardListSkeleton everywhere else; a line of
  // grey text here read as a different product mid-navigation.
  if (isLoading) return <CardListSkeleton count={4} />;

  if (isError) {
    return (
      <button
        type="button"
        onClick={retry}
        className="min-h-[var(--space-12)] self-start rounded-(--radius-md) border border-[var(--border-subtle)] px-[var(--space-4)] text-[length:var(--text-body-sm)]"
      >
        {t('materials.loadErrorRetry')}
      </button>
    );
  }

  // Desktop always has a category open — the rail is a selector, not a
  // gate — so it falls back to the first one.
  const selectedCategory = openCategory ?? (isMobile ? null : categories[0]?.category_key ?? null);
  const current = categories.find((c) => c.category_key === selectedCategory);
  const itemsInCategory = materials.filter((m) => m.category === selectedCategory);
  const searching = query.trim().length > 0;

  const rail = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        ...(isMobile ? {} : { position: 'sticky', top: 'var(--space-5)' }),
      }}
    >
      <label
        style={{
          height: isMobile ? 'var(--space-12)' : 'var(--space-10)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: '0 var(--space-3)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-medium)',
          background: 'var(--surface-raised)',
          color: 'var(--text-tertiary-soft)',
        }}
      >
        <Search size={16} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('materials.searchPlaceholder')}
          style={{
            flex: 1,
            minWidth: 0,
            height: '100%',
            border: 'none',
            background: 'transparent',
            outline: 'none',
            fontSize: 'var(--text-body-sm)',
            color: 'var(--text-primary)',
          }}
        />
      </label>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-0-5)' }}>
        {categories.map((category) => (
          <CategoryRow
            key={category.category_key}
            category={category}
            count={countsByCategory[category.category_key]?.total ?? 0}
            brokenCount={countsByCategory[category.category_key]?.broken ?? 0}
            active={!isMobile && category.category_key === selectedCategory}
            compact={!isMobile}
            priceLine={
              category.category_key === 'price_table' && priceTables.current
                ? t('priceTable.versionAndDate', {
                    version: priceTables.current.version,
                    from: new Date(priceTables.current.valid_from)
                      .toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
                  })
                : null
            }
            onOpen={() => setOpenCategory(category.category_key)}
            t={t}
          />
        ))}
      </div>

      {canManage && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          style={{
            height: 'var(--space-10)',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: 'var(--button-primary-bg)',
            color: 'var(--button-primary-text)',
            fontSize: 'var(--text-body-sm)',
            fontWeight: 'var(--font-semibold)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-2)',
            cursor: 'pointer',
          }}
        >
          <Plus size={16} />
          {t('materials.add')}
        </button>
      )}
    </div>
  );

  const panel = current && (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', minWidth: 0 }}>
      {isMobile && (
        <button
          type="button"
          onClick={() => setOpenCategory(null)}
          className="flex min-h-[var(--space-12)] items-center gap-[var(--space-2)] self-start text-[length:var(--text-body-sm)] text-[var(--text-secondary)]"
        >
          <ChevronLeft size={16} />
          {t('materials.backToCategories')}
        </button>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <span style={{ color: 'var(--text-secondary)', display: 'flex' }}>
          {(() => {
            const Icon = CATEGORY_ICON[current.category_key] ?? BookOpen;
            return <Icon size={22} />;
          })()}
        </span>
        <Text
          as="span"
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 'var(--text-2xl)',
            fontWeight: 'var(--font-semibold)',
            letterSpacing: 'var(--tracking-app-title)',
            color: 'var(--text-primary)',
          }}
        >
          {categoryName(current, t)}
        </Text>
      </div>

      {current.category_key === 'price_table' ? (
        <PriceTablePanel
          overview={priceTables}
          onOpenVersion={openPriceVersion}
          materialFor={(id) => materials.find((m) => m.material_id === id)}
          canManage={canManage}
          onPublishNew={() => setAdding(true)}
          onRename={canManage ? renameMaterial : undefined}
        />
      ) : itemsInCategory.length === 0 ? (
        <EmptyCategory
          category={current}
          canManage={canManage}
          onAdd={() => setAdding(true)}
          t={t}
        />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))',
            gap: 'var(--space-3)',
          }}
        >
          {itemsInCategory.map((material) => (
            <MaterialCard
              key={material.material_id}
              material={material}
              canManage={canManage}
              onOpen={setViewing}
              onRecheck={retry}
              onReplace={canManage ? () => setAdding(true) : undefined}
              onRename={canManage ? renameMaterial : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div
      data-testid="launch-materials-tab"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', minWidth: 0 }}
    >
      <StalePriceBanner
        overview={priceTables}
        onOpenCurrent={() => priceTables.current && openPriceVersion(priceTables.current.version)}
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'minmax(0,1fr)' : '280px minmax(0,1fr)',
          gap: 'var(--space-5)',
          alignItems: 'start',
        }}
      >
        {/* The rail carries the search box, so it stays put on the phone
            while a query is running — hiding it would take the field the
            reader is typing into off the screen. */}
        {(!isMobile || !openCategory) && rail}

        {searching ? (
          <SearchResults
            query={query}
            results={searchResults}
            labelFor={(key) => {
              const category = categories.find((c) => c.category_key === key);
              return category ? categoryName(category, t) : key;
            }}
            onOpen={setViewing}
            t={t}
          />
        ) : (
          (!isMobile || openCategory) && panel
        )}
      </div>

      <MaterialViewer material={viewing} onClose={() => setViewing(null)} />

      <AddMaterialSheet
        open={adding}
        agencyId={agencyId}
        launchId={launchId}
        categories={categories}
        onClose={() => setAdding(false)}
        onCreated={() => { retry(); priceTables.retry(); }}
      />
    </div>
  );
}
